import { CalendarDots, Copy, UsersThree } from "@phosphor-icons/react";
import { type Dispatch, type SetStateAction, useState } from "react";
import { useNavigate } from "react-router-dom";

import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import RenderIf from "common/components/RenderIf";
import RestrictedComponent from "common/components/RestrictedComponent";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "common/components/ui/Accordion";
import { Button } from "common/components/ui/Button";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";
import { Tag } from "common/components/ui/Tag";
import { CAMPAIGN_TYPES } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { formatDate } from "common/helpers/utils";
import type { Campaign } from "common/types";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../../constants";
import DeleteCampaign from "../../../CampaignActions/DeleteCampaign";
import DuplicateCampaign from "../../../CampaignActions/DuplicateCampaign";
import CampaignStateTag from "../../../CampaignStateTag";
import ProgressTag from "../ProgressTag";
import SuccessTag from "../SuccessTag";
import PreviewItem from "./PreviewItem";

interface CampaignPreviewProps {
  campaign: Campaign;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}

export default function CampaignPreview({
  campaign,
  isOpen,
  setIsOpen,
}: CampaignPreviewProps) {
  const { accounts_count } = useSelectedWorkspaceContext();
  const [currentStep, setCurrentStep] = useState<
    "preview" | "delete" | "duplicate"
  >("preview");
  const navigate = useNavigate();

  const {
    type,
    owners,
    created_at: createdAt,
    outreach_type: outreachType,
    name,
    state,
    id,
    target_count: targetCount,
    start_date: startDate,
    uses_connection_note: isUsingNote,
  } = campaign;
  const { icon: SubtypeIcon } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];
  const { label } = CAMPAIGN_TYPES[type];

  const isDuplicateDisabled = accounts_count === 0;

  let content = (
    <>
      <div className="mb-6 flex items-center gap-2">
        <CampaignTypeIcon
          type={campaign.type}
          variant={campaign.outreach_type === "drip" ? "drip" : "default"}
        />
        <div className="flex flex-1 flex-col gap-y-1">
          <h5 className="break-words text-md-bold">{name}</h5>

          <div className="flex items-center gap-1 text-black-500">
            <SubtypeIcon size={16} />
            <span className="line-clamp-1 break-all text-button-xs">
              {label} • {outreachType === "drip" ? "1st" : "2nd & 3rd+"}{" "}
              connections
            </span>
          </div>
        </div>
        <RestrictedComponent visibleFor={["campaigns_full"]}>
          <Button
            variant="secondary-black"
            intent="iconOnly"
            onClick={() => setCurrentStep("duplicate")}
            disabled={isDuplicateDisabled}
          >
            <Copy />
          </Button>
        </RestrictedComponent>
      </div>

      {/* State Label */}
      <PreviewItem
        label="Status"
        value={
          <CampaignStateTag
            size="md"
            state={state}
            owners={owners}
            startDate={startDate}
            isUsingNote={isUsingNote}
          />
        }
      />

      <PreviewItem
        label="Created at"
        value={
          <Tag variant="tertiary" leftIcon={<CalendarDots weight="fill" />}>
            {formatDate(createdAt)}
          </Tag>
        }
      />

      <RenderIf condition={state !== "INIT"}>
        <PreviewItem
          label="Targets"
          value={
            <Tag variant="tertiary" leftIcon={<UsersThree weight="fill" />}>
              {targetCount}
            </Tag>
          }
        />
      </RenderIf>

      {!(state === "INIT" || state === "CREATED") && (
        <>
          <PreviewItem
            label="Success"
            value={
              <SuccessTag
                campaignId={id}
                outreachType={outreachType}
                size="md"
              />
            }
          />
          <PreviewItem
            label="Progress %"
            value={<ProgressTag campaignId={id} size="md" />}
          />
        </>
      )}

      <RestrictedComponent visibleFor={["campaigns_full"]}>
        <Accordion
          type="single"
          className="-mx-4 border-b border-black-200"
          collapsible
        >
          <AccordionItem value="delete">
            <AccordionTrigger className="py-2.5 text-button-sm text-black-500 px-4 justify-between">
              Danger zone
            </AccordionTrigger>
            <AccordionContent className="flex justify-end py-3 pr-3 pl-2">
              <Button
                variant="outline-danger"
                onClick={() => setCurrentStep("delete")}
              >
                Delete Campaign
              </Button>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </RestrictedComponent>

      <Button className="ml-auto mt-8" size="lg" onClick={() => navigate(id)}>
        Campaign overview
      </Button>
    </>
  );

  if (currentStep === "delete") {
    content = (
      <DeleteCampaign
        campaignName={name}
        campaignId={id}
        onClose={() => setIsOpen(false)}
        onCancel={() => setCurrentStep("preview")}
      />
    );
  } else if (currentStep === "duplicate") {
    content = (
      <DuplicateCampaign
        campaignId={id}
        name={name}
        accountIds={owners}
        type={type}
        isDrip={outreachType === "drip"}
        onCancel={() => setCurrentStep("preview")}
      />
    );
  }

  return (
    <Drawer
      open={isOpen}
      onAfterClose={() => setCurrentStep("preview")}
      onOpenChange={setIsOpen}
    >
      <DrawerContent>{content}</DrawerContent>
    </Drawer>
  );
}
